"use client";

import * as React from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import { IconX } from "@tabler/icons-react";

import { cn } from "../../utils";

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      "al-fixed al-top-0 al-z-[1000] al-flex al-max-h-screen al-w-full al-flex-col-reverse al-p-4 sm:al-bottom-0 sm:al-right-0 sm:al-top-auto sm:al-flex-col md:al-max-w-[420px]",
      className
    )}
    role="region"
    aria-label="notifications"
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
  "al-group al-pointer-events-auto al-relative al-flex al-w-full al-items-center al-justify-between al-space-x-4 al-overflow-hidden al-rounded-md al-border al-p-6 al-pr-8 al-shadow-lg al-transition-all data-[swipe=cancel]:al-translate-x-0 data-[swipe=end]:al-translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:al-translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:al-transition-none data-[state=open]:al-animate-in data-[state=closed]:al-animate-out data-[swipe=end]:al-animate-out data-[state=closed]:al-fade-out-80 data-[state=closed]:al-slide-out-to-right-full data-[state=open]:al-slide-in-from-top-full data-[state=open]:sm:al-slide-in-from-bottom-full",
  {
    variants: {
      variant: {
        default: "al-border al-bg-background al-text-foreground",
        destructive:
          "al-destructive al-group al-border-destructive al-bg-destructive al-text-destructive-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> & VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      role="alert"
      aria-label={variant === "destructive" ? "error-notification" : "notification"}
      {...props}
    />
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "al-inline-flex al-h-8 al-shrink-0 al-items-center al-justify-center al-rounded-md al-border al-bg-transparent al-px-3 al-text-sm al-font-medium al-ring-offset-background al-transition-colors hover:al-bg-secondary focus:al-outline-none focus:al-ring-2 focus:al-ring-ring focus:al-ring-offset-2 disabled:al-pointer-events-none disabled:al-opacity-50 group-[.destructive]:al-border-muted/40 group-[.destructive]:hover:al-border-destructive/30 group-[.destructive]:hover:al-bg-destructive group-[.destructive]:hover:al-text-destructive-foreground group-[.destructive]:focus:al-ring-destructive",
      className
    )}
    role="button"
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      "al-absolute al-right-2 al-top-2 al-rounded-md al-p-1 al-text-foreground/50 al-opacity-0 al-transition-opacity hover:al-text-foreground focus:al-opacity-100 focus:al-outline-none focus:al-ring-2 group-hover:al-opacity-100 group-[.destructive]:al-text-red-300 group-[.destructive]:hover:al-text-red-50 group-[.destructive]:focus:al-ring-red-400 group-[.destructive]:focus:al-ring-offset-red-600",
      className
    )}
    toast-close=""
    role="button"
    aria-label="close notification"
    {...props}
  >
    <IconX className="al-h-4 al-w-4" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn("al-text-sm al-font-semibold", className)}
    role="heading"
    aria-level={2}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn("al-text-sm al-opacity-90", className)}
    role="status"
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
};
